/**
 * ACME-1960: Name the fields behind the opaque "Invalid/Blank fields." save toast.
 * Detection mirrors the Create/Edit/Clone save gates and syncGeneralInfoValid /
 * SchedulerView.checkifAllFieldsValid — do not invent extra rules here.
 */
import {
  hasNonEmptyTrimmed,
  isSourceLocationPathFormatValid,
  isUnchangedPersistedEditPath,
  isWorkflowNameFormatValid,
} from "./workflow-general-info-valid";

export type WorkflowSaveProblemKind = "required" | "invalid";

export type WorkflowSaveProblemTab =
  | "General Information"
  | "File Patterns"
  | "Scheduler";

export interface WorkflowSaveProblem {
  /** Toast label, pre-qualified when a bare name is ambiguous. */
  label: string;
  kind: WorkflowSaveProblemKind;
  /** Tab the field lives on, for the follow-up story's deep-link/focus work. */
  tab: WorkflowSaveProblemTab;
}

export interface CollectWorkflowSaveProblemsInput {
  workflowData: Record<string, unknown>;
  filepattern: {
    DataFilePattern?: unknown[];
    TriggerFilePattern?: unknown[];
  };
  schedulerData?: Record<string, unknown>;
  /** True only for PULL — mirrors the gate, which ignores scheduler on PUSH. */
  includeScheduler: boolean;
}

const FALLBACK_MESSAGE = "Invalid/Blank fields.";

function isPullTransfer(transferType: unknown): boolean {
  return String(transferType ?? "").trim().toLowerCase() === "pull";
}

function isBlankOrUndefined(value: unknown): boolean {
  return value === undefined || value === "" || value === null;
}

function kindLabel(kind: WorkflowSaveProblemKind): string {
  return kind === "required" ? "required" : "invalid format";
}

function collectGeneralInfoProblems(
  workflowData: Record<string, unknown>,
): WorkflowSaveProblem[] {
  const problems: WorkflowSaveProblem[] = [];
  const pull = isPullTransfer(workflowData.TransferType);
  const editPull = !!workflowData.isEdit && pull;

  if (!hasNonEmptyTrimmed(workflowData.WorkflowName)) {
    problems.push({
      label: "Workflow Name",
      kind: "required",
      tab: "General Information",
    });
  } else if (!editPull && !isWorkflowNameFormatValid(workflowData.WorkflowName)) {
    problems.push({
      label: "Workflow Name",
      kind: "invalid",
      tab: "General Information",
    });
  }

  if (!hasNonEmptyTrimmed(workflowData.TransferType)) {
    problems.push({
      label: "Transfer Type",
      kind: "required",
      tab: "General Information",
    });
  }

  if (!hasNonEmptyTrimmed(workflowData.BusinessUnitParticipant)) {
    problems.push({
      label: "Business Unit",
      kind: "required",
      tab: "General Information",
    });
  }

  if (!pull) {
    if (!hasNonEmptyTrimmed(workflowData.sourceLocationPath)) {
      problems.push({
        label: "Source Location Path",
        kind: "required",
        tab: "General Information",
      });
    } else if (
      !isSourceLocationPathFormatValid(workflowData.sourceLocationPath) &&
      // Edit-save accepts an unchanged persisted path even when it fails GI format, so
      // naming it here would accuse a field the gate deliberately let through.
      !isUnchangedPersistedEditPath(workflowData, workflowData.sourceLocationPath)
    ) {
      problems.push({
        label: "Source Location Path",
        kind: "invalid",
        tab: "General Information",
      });
    }
  }

  return problems;
}

function collectFilePatternProblems(filepattern: {
  DataFilePattern?: unknown[];
  TriggerFilePattern?: unknown[];
}): WorkflowSaveProblem[] {
  const problems: WorkflowSaveProblem[] = [];
  const data = filepattern.DataFilePattern ?? [];
  const trigger = filepattern.TriggerFilePattern ?? [];

  if (data.length <= 0) {
    problems.push({
      label: "Data File Pattern",
      kind: "required",
      tab: "File Patterns",
    });
  }
  if (trigger.length <= 0) {
    problems.push({
      label: "Trigger File Pattern",
      kind: "required",
      tab: "File Patterns",
    });
  }
  return problems;
}

/**
 * Mirrors SchedulerView.checkifAllFieldsValid but reports every failure
 * (the view's else-if chain stops at the first; OR-of-all is the same verdict).
 */
function collectSchedulerProblems(
  schedulerData: Record<string, unknown> | undefined,
): WorkflowSaveProblem[] {
  if (!schedulerData) {
    return [
      {
        label: "Scheduler Configuration",
        kind: "required",
        tab: "Scheduler",
      },
    ];
  }

  const problems: WorkflowSaveProblem[] = [];

  if (!schedulerData.StartDate) {
    problems.push({
      label: "Scheduler Start Date",
      kind: "required",
      tab: "Scheduler",
    });
  }
  if (!schedulerData.Type) {
    problems.push({
      label: "Scheduler Schedule Type",
      kind: "required",
      tab: "Scheduler",
    });
  }

  const typeKey = String(schedulerData.Type ?? "")
    .trim()
    .toLowerCase();

  if (typeKey === "rate") {
    if (isBlankOrUndefined(schedulerData.RateValue)) {
      problems.push({
        label: "Scheduler Rate Expression",
        kind: "required",
        tab: "Scheduler",
      });
    }
    if (isBlankOrUndefined(schedulerData.RateConfig)) {
      problems.push({
        label: "Scheduler Rate Config",
        kind: "required",
        tab: "Scheduler",
      });
    }
  } else if (typeKey === "cron") {
    const cronFields: Array<[string, string]> = [
      ["CronHour", "Scheduler Cron Hour"],
      ["CronMonthDay", "Scheduler Cron Month Day"],
      ["CronMonth", "Scheduler Cron Month"],
      ["CronWeekDay", "Scheduler Cron Week Day"],
      ["CronYear", "Scheduler Cron Year"],
    ];
    for (const [key, label] of cronFields) {
      if (isBlankOrUndefined(schedulerData[key])) {
        problems.push({ label, kind: "required", tab: "Scheduler" });
      }
    }
  }

  return problems;
}

/**
 * Collect named problems that match the Create/Edit/Clone save gate.
 * Call after syncGeneralInfoValid so GI field values are trimmed.
 */
export function collectWorkflowSaveProblems(
  input: CollectWorkflowSaveProblemsInput,
): WorkflowSaveProblem[] {
  const problems: WorkflowSaveProblem[] = [];
  problems.push(...collectGeneralInfoProblems(input.workflowData));
  problems.push(...collectFilePatternProblems(input.filepattern));
  if (input.includeScheduler) {
    // Gate only reads schedulerData.valid — dig into fields only when that bit is false
    // so we do not name scheduler fields while valid is still true (common in unit fixtures).
    if (!input.schedulerData?.valid) {
      problems.push(...collectSchedulerProblems(input.schedulerData));
    }
  }
  return problems;
}

/**
 * Format for jha-toast (single wrapping sentence, no newlines).
 * Names the fields directly — the generic prefix only survives as the
 * fallback for when the gate fired but no field could be identified.
 */
export function formatWorkflowSaveProblems(
  problems: WorkflowSaveProblem[],
  maxNamed = 2,
): string {
  if (!problems.length) {
    return FALLBACK_MESSAGE;
  }

  const named = problems.slice(0, maxNamed);
  const namedText = named
    .map((p) => `${p.label} (${kindLabel(p.kind)})`)
    .join(", ");
  const remaining = problems.length - named.length;

  if (remaining <= 0) {
    return `${namedText}.`;
  }

  return `${namedText}, and ${remaining} more. Check the tabs marked with a warning icon.`;
}

/**
 * Tabs whose gate condition is false, regardless of whether a field could be named.
 * Mirrors the three OR-branches of the Create/Edit/Clone save gate.
 */
function collectFailingGateAreas(
  input: CollectWorkflowSaveProblemsInput,
): WorkflowSaveProblemTab[] {
  const areas: WorkflowSaveProblemTab[] = [];

  if (!input.workflowData.valid) {
    areas.push("General Information");
  }
  const data = input.filepattern.DataFilePattern ?? [];
  const trigger = input.filepattern.TriggerFilePattern ?? [];
  if (data.length <= 0 || trigger.length <= 0) {
    areas.push("File Patterns");
  }
  if (input.includeScheduler && !input.schedulerData?.valid) {
    areas.push("Scheduler");
  }

  return areas;
}

/**
 * Last resort when the gate blocked the save but no field could be named
 * (e.g. a stale `schedulerData.valid`). Still tells the user where to look —
 * never emit the bare "Invalid/Blank fields." if a tab can be identified.
 */
export function formatUnnamedGateFailure(areas: WorkflowSaveProblemTab[]): string {
  if (!areas.length) {
    return FALLBACK_MESSAGE;
  }

  const list =
    areas.length === 1
      ? areas[0]
      : `${areas.slice(0, -1).join(", ")} and ${areas[areas.length - 1]}`;

  return areas.length === 1
    ? `${list} needs attention. Open that tab, re-check its fields, then save again.`
    : `${list} need attention. Open those tabs, re-check their fields, then save again.`;
}

/** Convenience for Create/Edit/Clone save handlers. */
export function workflowSaveInvalidBlankMessage(input: CollectWorkflowSaveProblemsInput): string {
  const problems = collectWorkflowSaveProblems(input);
  if (problems.length) {
    return formatWorkflowSaveProblems(problems);
  }
  return formatUnnamedGateFailure(collectFailingGateAreas(input));
}
